import { useEffect, useMemo, useState } from 'react'
import { Link, useLocation, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { ArrowLeft, BadgeCheck, CalendarClock, CreditCard, ExternalLink, Loader2, Mail, Plus, ReceiptIndianRupee, Trash2, UserRound, WalletCards } from 'lucide-react'

import { useConfirmDialog } from '@/components/common/confirm-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { OrderItemMedia } from '@/components/orders/order-item-media'
import { OrderItemAssets } from '@/components/orders/order-item-assets'
import { OrderResourcePlanningPanel } from '@/components/orders/order-resource-planning-panel'
import { OrderSettlementSummaryPanel } from '@/components/orders/order-settlement-summary'
import { ComplaintsPanel } from '@/components/jobs/job-complaints-panel'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import { ordersApi, resolveOrderPrimaryKey } from '@/services/orders'
import type { StoreOrderDetail } from '@/types'
import { OrgWallet, orgWalletApi, getOrgWalletLabel } from '@/services/org-wallet'
import { getErrorMessage } from '@/lib/api'
import { getServiceInstanceIdFromSearch, withServiceInstanceId } from '@/lib/service-instance'
import { resolveJobNavigationServiceInstanceId } from '@/lib/planner-job-instance'
import { useOrganizationDateTimeFormat } from '@/lib/org-date-locale'
import { formatCurrency as formatAppCurrency } from '@/lib/utils'
import { useAuthStore } from '@/stores/auth-store'

const parseDateValue = (value?: string | null) => {
  const raw = String(value ?? '').trim()
  if (!raw) {
    return null
  }

  const match = raw.match(/^(\d{2})-(\d{2})-(\d{4})(?:\s+(\d{2}):(\d{2})(?::(\d{2}))?)?$/)
  if (match) {
    const [, day, month, year, hour = '0', minute = '0', second = '0'] = match
    const parsed = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute), Number(second)))
    return Number.isNaN(parsed.getTime()) ? null : parsed
  }

  const hasExplicitTimeZone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(raw)
  const hasClockTime = /\d{2}:\d{2}/.test(raw)
  const normalized = raw.includes(' ') ? raw.replace(' ', 'T') : raw

  const parsed = hasClockTime && !hasExplicitTimeZone
    ? new Date(`${normalized}Z`)
    : new Date(normalized)

  return Number.isNaN(parsed.getTime()) ? null : parsed
}

const parseAmount = (amount?: string | number | null) => {
  if (typeof amount === 'number') {
    return Number.isFinite(amount) ? amount : 0
  }

  if (typeof amount === 'string') {
    const parsed = Number(amount.replace(/[^0-9.-]+/g, ''))
    return Number.isFinite(parsed) ? parsed : 0
  }

  return 0
}

const formatMoney = (amount: number, currency?: string | null) => {
  const marker = String(currency ?? '').trim()
  const safeAmount = Number.isFinite(amount) ? amount : 0

  if (/^[A-Za-z]{3}$/.test(marker)) {
    return formatAppCurrency(safeAmount, marker.toUpperCase())
  }

  if (marker) {
    const sign = safeAmount < 0 ? '-' : ''
    const formatted = Math.abs(safeAmount).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
    return `${sign}${marker}${formatted}`
  }

  return formatAppCurrency(safeAmount)
}

// const formatMaybeDate = (value?: string | null) => {
//   if (!value) {
//     return '-'
//   }
//   const parsed = new Date(value)
//   return Number.isNaN(parsed.getTime()) ? value : formatDate(parsed)
// }

const textFromRecord = (record: Record<string, unknown> | null | undefined, keys: string[]) => {
  if (!record) {
    return null
  }
  for (const key of keys) {
    const value = record[key]
    if (value != null && String(value).trim() !== '') {
      return String(value)
    }
  }
  return null
}

const formatPaymentStatus = (status?: string | number | null) => {
  if (status == null || status === '') {
    return '-'
  }

  const text = String(status).trim()
  const normalized = text.toLowerCase().replace(/[^a-z0-9]+/g, '_')

  if (normalized.includes('success') || normalized.includes('verified') || normalized === '3') {
    return 'Success'
  }

  if (normalized.includes('fail') || normalized.includes('cancel') || normalized.includes('reject') || normalized.includes('declin')) {
    return 'Failure'
  }

  if (normalized.includes('pending') || normalized.includes('initiated') || normalized === '9') {
    return 'Pending'
  }

  return text
    .replace(/^payment[_\s-]*/i, '')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase()
    .replace(/\b\w/g, (letter) => letter.toUpperCase()) || '-'
}

const isSuccessfulPaymentStatus = (status?: string | number | null) => {
  if (status == null || status === '') {
    return false
  }

  const normalized = String(status).trim().toLowerCase().replace(/[^a-z0-9]+/g, '_')
  return normalized.includes('success') || normalized.includes('verified') || normalized === '3'
}

const isEncryptedToken = (value?: string | null) => {
  const token = String(value ?? '').trim()
  return token.length > 0 && (/^gAAAA/i.test(token) || token.length > 48)
}

const formatPaymentChannelLabel = (value?: string | number | null) => {
  const text = String(value ?? '').trim()
  if (!text) {
    return ''
  }

  return text
    .replace(/^payment[_\s-]*/i, '')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase()
    .replace(/\b\w/g, (letter) => letter.toUpperCase())
}

const getPaymentDisplayReference = (payment: {
  payment_reference_id?: string | null
  id?: string | number | null
  _id?: string | number | null
}) => {
  const reference = String(payment.payment_reference_id ?? '').trim()
  if (reference) {
    return reference
  }

  const maskedId = payment._id
  if (maskedId != null && String(maskedId).trim() !== '') {
    return `Payment #${maskedId}`
  }

  const rawId = String(payment.id ?? '').trim()
  if (rawId && !isEncryptedToken(rawId)) {
    return rawId
  }

  return 'Payment'
}

const toOrderItemId = (value: unknown): string | number | null => {
  if (typeof value === 'string' || typeof value === 'number') {
    return value
  }
  return null
}

const isCompletedOrderStatus = (status?: string | number | null, label?: string | null) => {
  const normalizedStatus = status == null ? '' : String(status).trim().toLowerCase()
  const normalizedLabel = label == null ? '' : label.trim().toLowerCase()

  return normalizedStatus === '21' || normalizedStatus === 'order_completed' || normalizedLabel === 'completed'
}

const isMediaEditableOrderStatus = (status?: string | number | null, label?: string | null) => {
  const numeric = Number(status)
  if (Number.isFinite(numeric)) {
    return numeric < 2
  }

  const normalized = String(label ?? status ?? '').trim().toLowerCase()
  if (!normalized) {
    return true
  }

  return !(normalized.includes('placed') || normalized.includes('approved') || normalized.includes('preparing') || normalized.includes('shipping') || normalized.includes('completed'))
}

const canResendConfirmationEmail = (status?: string | number | null, label?: string | null) => {
  const numeric = Number(status)
  if (Number.isFinite(numeric)) {
    return numeric !== 1 && numeric !== 12 && numeric !== 13
  }

  const normalized = String(label ?? status ?? '').trim().toLowerCase()
  if (!normalized) {
    return true
  }

  return !(
    normalized.includes('initiated')
    || normalized.includes('cancel')
    || normalized.includes('reject')
  )
}

const resolveOrderCustomerEmail = (order: {
  email?: string | null
  customer_email?: string | null
  user_data?: { email?: string | null } | null
}) => {
  return String(order.email ?? order.customer_email ?? order.user_data?.email ?? '').trim()
}

const isPosSourceOrder = (order: Pick<StoreOrderDetail, 'source_type' | 'source_type_str'>) => {
  const sourceNum = Number(order.source_type)
  if (sourceNum === 1 || sourceNum === 4) {
    return true
  }
  const label = String(order.source_type_str ?? '').trim().toUpperCase()
  return label === 'POS' || label === 'POS_DEVICE' || label.includes('POS')
}

const parsePosCheckoutMeta = (raw?: string | null) => {
  const trimmed = String(raw ?? '').trim()
  if (!trimmed || trimmed.startsWith('user_')) {
    return { name: '', reg: '' }
  }
  const lowered = trimmed.toLowerCase()
  if (lowered.split('-').length >= 5 && trimmed.length >= 32 && !trimmed.includes('|')) {
    return { name: '', reg: '' }
  }
  if (!trimmed.includes('|') && !lowered.includes('tips=') && !lowered.includes('tax=')) {
    return { name: trimmed, reg: '' }
  }
  let name = ''
  let reg = ''
  let indexed = 0
  for (const part of trimmed.split('|')) {
    const piece = part.trim()
    if (!piece) continue
    const lowerPiece = piece.toLowerCase()
    if (lowerPiece.startsWith('tips=') || lowerPiece.startsWith('tax=') || lowerPiece.startsWith('disc=') || lowerPiece.startsWith('del=')) {
      continue
    }
    if (indexed === 0) {
      name = piece
      indexed += 1
    } else if (indexed === 1) {
      reg = piece
      indexed += 1
    }
  }
  return { name, reg }
}

const resolveOrderStaff = (order: StoreOrderDetail) => {
  const fromNotes = parsePosCheckoutMeta(order.notes)
  const name = String(
    order.staff_name
    || fromNotes.name
    || order.created_by_data?.name
    || '',
  ).trim()
  const reg = String(
    order.staff_reg
    || fromNotes.reg
    || order.created_by_data?.usr_reg_num
    || '',
  ).trim()
  const lowered = name.toLowerCase()
  if (!name && !reg) {
    return null
  }
  if (lowered && ['import', 'pos', 'system', '—', '-', 'none', 'null'].includes(lowered)) {
    return reg ? { name: '—', reg } : null
  }
  return { name: name || '—', reg }
}

const paymentMethodConfigFromText = (text: string) => {
  const normalized = text.toLowerCase()

  if (normalized.includes('cash')) {
    return { payment_method: 1, payment_mode: 8 }
  }

  if (normalized.includes('pay later') || normalized.includes('pay_later') || normalized.includes('later')) {
    return { payment_method: 4, payment_mode: 10 }
  }

  if (normalized.includes('upi') || normalized.includes('google') || normalized.includes('phonepe')) {
    return { payment_method: 3, payment_mode: 2 }
  }

  if (normalized.includes('card') || normalized.includes('debit') || normalized.includes('credit') || normalized.includes('swipe')) {
    return { payment_method: 2, payment_mode: 6 }
  }

  return { payment_method: 3, payment_mode: 1 }
}

const getWalletLabel = (wallet: OrgWallet) => getOrgWalletLabel(wallet)

const orderRecurrenceRuleLabels: Record<string, string> = {
  daily: 'Daily',
  weekly: 'Weekly',
  bi_weekly: 'Bi-weekly',
  monthly: 'Monthly',
  every_n_days: 'Every N days',
  weekly_days: 'Weekly on selected days',
  monthly_day: 'Monthly on a specific day',
}

const orderRecurrenceWeekdayLabels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

const describeOrderRecurrence = (recurrence: NonNullable<StoreOrderDetail['recurrence']>) => {
  if (recurrence.rule === 'every_n_days' && recurrence.interval_days) {
    return `Every ${recurrence.interval_days} days`
  }
  if (recurrence.rule === 'weekly_days' && recurrence.weekdays?.length) {
    const labels = recurrence.weekdays
      .map((day) => orderRecurrenceWeekdayLabels[day] || '')
      .filter(Boolean)
    if (labels.length > 0) {
      return `Weekly on ${labels.join(', ')}`
    }
  }
  if (recurrence.rule === 'monthly_day' && recurrence.month_day) {
    return `Monthly on day ${recurrence.month_day}`
  }
  return orderRecurrenceRuleLabels[recurrence.rule] || 'Recurring'
}

export default function CommerceOrderDetailPage() {
  const queryClient = useQueryClient()
  const { confirm, ConfirmDialog } = useConfirmDialog()
  const { referenceId } = useParams<{ referenceId: string }>()
  const location = useLocation()
  const serviceInstanceId = useAuthStore((state) => state.serviceInstanceId)
  const effectiveServiceInstanceId = getServiceInstanceIdFromSearch(location.search) ?? serviceInstanceId
  const { locale: orgLocale, timeZone: orgTimeZone } = useOrganizationDateTimeFormat(effectiveServiceInstanceId)
  const [isAddPaymentOpen, setIsAddPaymentOpen] = useState(false)
  const [isResendConfirmationOpen, setIsResendConfirmationOpen] = useState(false)
  const [resendEmail, setResendEmail] = useState('')
  const [selectedWalletId, setSelectedWalletId] = useState('')
  const [paymentAmount, setPaymentAmount] = useState('')
  const orderQuery = useQuery({
    queryKey: ['order-detail', referenceId, effectiveServiceInstanceId ?? 'none'],
    queryFn: () => ordersApi.getOrderDetail(referenceId ?? '', effectiveServiceInstanceId),
    enabled: Boolean(referenceId),
  })

  const order = orderQuery.data
  const jobNavigationServiceInstanceId = useMemo(() => resolveJobNavigationServiceInstanceId({
    jobServiceInstanceId: order?.job_service_instance_id,
    jobsSummary: order?.jobs_summary,
    bookingServiceInstanceId: order?.service_instance_id ?? effectiveServiceInstanceId,
  }), [effectiveServiceInstanceId, order?.job_service_instance_id, order?.jobs_summary, order?.service_instance_id])
  const getJobDetailsPath = (
    jobId: string | number,
    jobServiceInstanceId?: string | number | null,
  ) => withServiceInstanceId(
    `/job/${jobId}`,
    (jobServiceInstanceId ?? jobNavigationServiceInstanceId ?? effectiveServiceInstanceId) != null
      ? String(jobServiceInstanceId ?? jobNavigationServiceInstanceId ?? effectiveServiceInstanceId)
      : undefined,
  )

  const backTarget = `/services/orders${location.search}`
  const offerRows = useMemo(() => order?.offers_applied ?? [], [order?.offers_applied])
  const paymentsQuery = useQuery({
    queryKey: ['order-payments', order?.id],
    queryFn: () => ordersApi.listOrderPayments(order?.id ?? ''),
    enabled: Boolean(order?.id),
  })
  const paymentRows = paymentsQuery.data?.results ?? []
  const summaryCurrency = order?.totals.find((line) => line.currency)?.currency ?? paymentRows.find((payment) => payment.currency)?.currency
  const finalTotalAmount = parseAmount(order?.final_total ?? order?.totals.at(-1)?.amount ?? order?.total)
  const successfulPaidAmount = paymentRows
    .filter((payment) => isSuccessfulPaymentStatus(payment.payment_status))
    .reduce((sum, payment) => sum + parseAmount(payment.amount), 0)
  const balanceAmount = paymentsQuery.isLoading
    ? null
    : Math.max(finalTotalAmount - successfulPaidAmount, 0)
  const fallbackBalanceAmount = order?.is_paid ? 0 : finalTotalAmount
  const isPaymentSettled = order?.is_paid === true || (balanceAmount != null && finalTotalAmount > 0 && balanceAmount <= 0.005)
  const canAddPayment = !isPaymentSettled && (balanceAmount == null ? fallbackBalanceAmount : balanceAmount) > 0.005
  const canDeletePayments = order ? !isCompletedOrderStatus(order.status, order.status_label) : false
  const canResendConfirmation = order ? canResendConfirmationEmail(order.status, order.status_label) : false
  const customerEmail = order ? resolveOrderCustomerEmail(order) : ''
  const staffDetails = order && isPosSourceOrder(order) ? resolveOrderStaff(order) : null
  const hasLinkedJobs = useMemo(() => {
    if (!order) {
      return false
    }
    const summaryCount = Array.isArray(order.jobs_summary) ? order.jobs_summary.length : 0
    if (summaryCount > 0) {
      return true
    }
    return order.order_items.some((item) => item.has_job && item.job_id != null)
  }, [order])
  const primaryLinkedJob = useMemo(() => {
    if (!order) {
      return null
    }

    if (Array.isArray(order.jobs_summary) && order.jobs_summary.length > 0) {
      const first = order.jobs_summary[0]
      if (first?.id != null) {
        return {
          id: first.id,
          jobRef: first.job_ref ?? null,
          serviceInstanceId: first.service_instance_pk ?? first.service_instance_id ?? order.job_service_instance_id ?? null,
        }
      }
    }

    if (order.job_id != null) {
      return {
        id: order.job_id,
        jobRef: null,
        serviceInstanceId: order.job_service_instance_id ?? null,
      }
    }

    if (Array.isArray(order.job_ids) && order.job_ids.length > 0 && order.job_ids[0] != null) {
      return {
        id: order.job_ids[0],
        jobRef: null,
        serviceInstanceId: order.job_service_instance_id ?? null,
      }
    }

    const itemWithJob = order.order_items.find((item) => item.has_job && item.job_id != null)
    if (itemWithJob?.job_id != null) {
      return {
        id: itemWithJob.job_id,
        jobRef: itemWithJob.job_ref ?? null,
        serviceInstanceId: itemWithJob.job_service_instance_id ?? order.job_service_instance_id ?? null,
      }
    }

    return null
  }, [order])
  const walletOrgInstanceId = String(order?.service_instance_id ?? effectiveServiceInstanceId ?? '').trim()
  const walletMethodsQuery = useQuery({
    queryKey: ['booking-payment-wallet-methods', walletOrgInstanceId],
    queryFn: () => orgWalletApi.listWallets(walletOrgInstanceId, {
      orgInstanceId: walletOrgInstanceId,
    }),
    enabled: isAddPaymentOpen && Boolean(walletOrgInstanceId),
    staleTime: 120_000,
  })
  const walletMethods = (walletMethodsQuery.data ?? []).filter((wallet) => wallet.active !== false)
  const selectedWallet = walletMethods.find((wallet) => String(wallet.id) === selectedWalletId) ?? null
  const renderDate = (
    value?: string| null,
    type: "display" | "date" = "display"
  ) => {
    if (!value) {
      return type === "date" ? null : "-";
    }

    const parsed = parseDateValue(value)
    if (!parsed) {
      return type === "date" ? null : "-";
    }

    return type === "date"
      ? parsed
      : new Intl.DateTimeFormat(orgLocale, {
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
        timeZone: orgTimeZone,
      }).format(parsed);
  };
  useEffect(() => {
    if (!isAddPaymentOpen) {
      return
    }

    const amount = balanceAmount == null ? fallbackBalanceAmount : balanceAmount
    setPaymentAmount(amount > 0 ? amount.toFixed(2) : '')
    setSelectedWalletId('')
  }, [balanceAmount, fallbackBalanceAmount, isAddPaymentOpen])

  const createPaymentMutation = useMutation({
    mutationFn: () => {
      if (!order?.id || !selectedWallet) {
        throw new Error('Select a payment method')
      }

      const amount = parseAmount(paymentAmount)
      if (amount <= 0) {
        throw new Error('Enter a valid payment amount')
      }

      return ordersApi.createOrderPayment({
        order: order.id,
        amount: amount.toFixed(2),
        payment_service: 8,
        ...paymentMethodConfigFromText(getWalletLabel(selectedWallet)),
      })
    },
    onSuccess: async () => {
      toast.success('Payment added')
      setIsAddPaymentOpen(false)
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['order-payments', order?.id] }),
        queryClient.invalidateQueries({ queryKey: ['order-detail', referenceId] }),
      ])
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const deletePaymentMutation = useMutation({
    mutationFn: (paymentId: string | number) => ordersApi.deleteOrderPayment(paymentId),
    onSuccess: async () => {
      toast.success('Payment deleted')
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['order-payments', order?.id] }),
        queryClient.invalidateQueries({ queryKey: ['order-detail', referenceId] }),
      ])
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const resendConfirmationMutation = useMutation({
    mutationFn: () => {
      if (!order) {
        throw new Error('Booking not loaded')
      }

      const orderPk = resolveOrderPrimaryKey(order)
      if (orderPk == null) {
        throw new Error('Unable to resolve booking id for notification')
      }

      const email = resendEmail.trim()
      if (!email) {
        throw new Error('Enter a customer email address')
      }

      return ordersApi.resendConfirmationEmail({
        order: orderPk,
        email,
      })
    },
    onSuccess: (payload) => {
      toast.success(payload?.message || 'Confirmation email sent')
      setIsResendConfirmationOpen(false)
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const openResendConfirmationDialog = () => {
    setResendEmail(customerEmail)
    setIsResendConfirmationOpen(true)
  }

  const handleDeletePayment = (paymentId: string | number) => {
    if (!canDeletePayments) {
      toast.error('Payments cannot be deleted after the booking is completed')
      return
    }

    confirm({
      title: 'Delete payment record',
      description: 'Delete this payment record?',
      confirmLabel: 'Delete',
      destructive: true,
      onConfirm: () => deletePaymentMutation.mutate(paymentId),
    })
  }

  if (orderQuery.isLoading) {
    return <div className="flex min-h-[360px] items-center justify-center text-sm text-muted-foreground"><Loader2 className="mr-2 h-4 w-4 animate-spin" />Loading booking details...</div>
  }

  if (orderQuery.isError) {
    return <div className="rounded-xl border border-destructive/20 bg-destructive/5 p-4 text-sm text-destructive">{getErrorMessage(orderQuery.error)}</div>
  }

  if (!order) {
    return <div className="rounded-xl border border-border/70 p-4 text-sm text-muted-foreground">Booking not found.</div>
  }

  return (
    <div className="space-y-6">
      <ConfirmDialog />
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="space-y-1">
          <Button asChild variant="ghost" className="-ml-3 w-fit gap-2">
            <Link to={backTarget}><ArrowLeft className="h-4 w-4" />Bookings</Link>
          </Button>
          <h1 className="text-2xl font-semibold tracking-tight">Booking #{order.reference_id}</h1>
          <div className="flex flex-wrap gap-2">
            {order.status_label ? <Badge variant="outline">{order.status_label}</Badge> : null}
            {order.type_label ? <Badge variant="secondary">{order.type_label}</Badge> : null}
            {typeof order.is_paid === 'boolean' ? <Badge variant={order.is_paid ? 'default' : 'outline'}>{order.is_paid ? 'Paid' : 'Unpaid'}</Badge> : null}
          </div>
          {primaryLinkedJob ? (
            <Button asChild variant="link" className="h-auto p-0 text-sm font-medium">
              <Link to={getJobDetailsPath(primaryLinkedJob.id, primaryLinkedJob.serviceInstanceId)}>
                Created job {primaryLinkedJob.jobRef ? `#${primaryLinkedJob.jobRef}` : `#${primaryLinkedJob.id}`}
                <ExternalLink className="ml-1 h-3.5 w-3.5" />
              </Link>
            </Button>
          ) : null}
        </div>
      </div>

      <section className="grid gap-4 lg:grid-cols-3">
        <Card className="relative overflow-hidden">
          <CardContent className="flex min-h-[112px] items-center gap-4 p-5">
            <ReceiptIndianRupee className="h-5 w-5 text-emerald-700" />
            <div className="flex-1 space-y-1">
              <div>
                <p className="text-sm text-muted-foreground">Total</p>
                <p className="text-2xl font-semibold">{formatMoney(finalTotalAmount, summaryCurrency)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Balance due</p>
                <p className="text-lg font-semibold text-rose-700">{paymentsQuery.isError ? formatMoney(fallbackBalanceAmount, summaryCurrency) : balanceAmount == null ? '-' : formatMoney(balanceAmount, summaryCurrency)}</p>
              </div>
            </div>
            <WalletCards className="h-4 w-4 text-rose-400" />
          </CardContent>
          <div className={`pointer-events-none absolute right-3 top-3 rotate-[-12deg] rounded-md border-[2px] px-3 py-1 text-sm font-black uppercase tracking-wide opacity-90 ${isPaymentSettled ? 'border-emerald-600 bg-emerald-50/80 text-emerald-700' : 'border-rose-500 bg-rose-50/80 text-rose-700'}`}>
            {isPaymentSettled ? 'Paid' : 'Unpaid'}
          </div>
        </Card>
       <Card>
  <CardContent className="flex items-start gap-4 p-5">
    <UserRound className="h-5 w-5 shrink-0 text-sky-700" />

    <div className="min-w-0 flex-1 space-y-2">
      <div>
        <p className="text-sm text-muted-foreground">
          Customer
        </p>
        <p className="font-semibold">
          {order.full_name || order.customer_name || '-'}
        </p>
        <p className="text-xs text-muted-foreground">
          {order.mobile || order.customer_phone || '-'}
        </p>
        {customerEmail ? (
          <p className="text-xs text-muted-foreground">{customerEmail}</p>
        ) : null}
      </div>

      <Button
        type="button"
        size="sm"
        variant="outline"
        className="h-8"
        disabled={!canResendConfirmation || resendConfirmationMutation.isPending}
        onClick={openResendConfirmationDialog}
      >
        {resendConfirmationMutation.isPending ? (
          <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
        ) : (
          <Mail className="mr-2 h-3.5 w-3.5" />
        )}
        Resend confirmation
      </Button>
      {!canResendConfirmation ? (
        <p className="text-xs text-muted-foreground">
          Confirmation cannot be resent for initiated, cancelled, or rejected bookings.
        </p>
      ) : null}

      {order.delivery_address_data ? (
        <div className="border-t pt-2">
          <p className="text-xs text-muted-foreground">
            Delivery Address
          </p>

          <p className="text-sm leading-6">
            {[
              order.delivery_address_data.door_no,
              order.delivery_address_data.building_name,
              order.delivery_address_data.street_line,
             (order.delivery_address_data.country_data as { name?: string })?.name,
              order.delivery_address_data.zipcode,
            ]
              .filter(Boolean)
              .join(', ')}
          </p>
        </div>
      ) : null}
    </div>
  </CardContent>
</Card>

        {staffDetails ? (
          <Card>
            <CardContent className="flex items-start gap-4 p-5">
              <BadgeCheck className="h-5 w-5 shrink-0 text-violet-700" />
              <div className="min-w-0 flex-1 space-y-1">
                <p className="text-sm text-muted-foreground">Staff</p>
                <p className="font-semibold">{staffDetails.name}</p>
                {staffDetails.reg ? (
                  <p className="text-xs text-muted-foreground">Reg no. {staffDetails.reg}</p>
                ) : null}
              </div>
            </CardContent>
          </Card>
        ) : null}

        <Card><CardContent className="flex items-center gap-4 p-5"><CalendarClock className="h-5 w-5 text-amber-700" /><div><p className="text-sm text-muted-foreground">Booking time</p><p className="text-lg font-semibold">
          {renderDate(order.order_time, "display") as string}</p></div></CardContent></Card>
      </section>

      {order.recurrence ? (
        <Card>
          <CardContent className="flex items-start gap-4 p-5">
            <CalendarClock className="h-5 w-5 shrink-0 text-violet-700" />
            <div className="grid flex-1 gap-3 sm:grid-cols-3">
              <div>
                <p className="text-sm text-muted-foreground">Recurring series</p>
                <p className="font-semibold">{describeOrderRecurrence(order.recurrence)}</p>
                <p className="text-xs text-muted-foreground">
                  {order.recurrence.end_date
                    ? `Until ${order.recurrence.end_date}`
                    : order.recurrence.occurrence_count
                      ? `${order.recurrence.occurrence_count} occurrences`
                      : 'Open-ended'}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Payment plan</p>
                <p className="font-semibold">
                  {order.recurrence.payment_plan === 'upfront' ? 'Paid upfront' : 'Pay per occurrence'}
                </p>
                <p className="text-xs text-muted-foreground">
                  {order.recurrence.payment_plan === 'upfront'
                    ? 'The full series was charged at checkout.'
                    : 'Each occurrence is invoiced when its job completes.'}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Per occurrence</p>
                <p className="font-semibold">
                  {formatMoney(parseAmount(order.recurrence.per_occurrence_total), summaryCurrency)}
                </p>
                {Array.isArray(order.jobs_summary) && order.jobs_summary.length > 0 ? (
                  <p className="text-xs text-muted-foreground">
                    {order.jobs_summary.filter((entry) => entry.status === 'completed').length} of{' '}
                    {order.jobs_summary.length} occurrence jobs completed
                  </p>
                ) : null}
              </div>
            </div>
          </CardContent>
        </Card>
      ) : null}

      <section className="grid gap-4 xl:grid-cols-[minmax(0,1.4fr)_minmax(320px,0.8fr)]">
        {/* Left column: Products */}
        <Card>
          <CardHeader><CardTitle>Products</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {order.order_items.length === 0 ? (
              <p className="text-sm text-muted-foreground">No products on this booking.</p>
            ) : (
              order.order_items.map((item, index) => {
                const offerTitle = item.offer_title || textFromRecord(item.applied_offer_data, ['title', 'offer', 'name'])
                const includedFeatures = (item.included_features ?? []).map((feature) => String(feature?.name || '').trim()).filter(Boolean)
                const excludedFeatures = (item.excluded_features ?? []).map((feature) => String(feature?.name || '').trim()).filter(Boolean)
                const itemRecord = item as Record<string, unknown>
                const orderItemId =
                  toOrderItemId(item.id) ??
                  toOrderItemId(itemRecord.order_item) ??
                  toOrderItemId(itemRecord.order_item_id) ??
                  toOrderItemId(item._id) ??
                  toOrderItemId(itemRecord._order_item) ??
                  null
                const canEditMedia = isMediaEditableOrderStatus(order.status, order.status_label)
                return (
                  <div key={`${item.product_name ?? 'item'}-${index}`} className="rounded-xl border border-border/70 p-4">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div className="min-w-0">
                        <p className="font-medium">{item.product_name || 'Unnamed product'}</p>
                        <p className="text-sm text-muted-foreground">{[item.product_variant_name, ...(item.addon_names ?? [])].filter(Boolean).join(' • ') || '-'}</p>
                        {offerTitle || item.discount_value ? (
                          <Badge variant="secondary" className="mt-2 border-emerald-200 bg-emerald-50 text-emerald-700">
                            {offerTitle || 'Offer'}{item.discount_value ? ` -${item.discount_value}${String(item.discount_type) === '1' ? '%' : ''}` : ''}
                          </Badge>
                        ) : null}
                      </div>
                      <div className="text-sm sm:text-right">
                        <p><span className="text-muted-foreground">Qty</span> {item.qty ?? '-'}</p>
                        <p><span className="text-muted-foreground">Unit</span> {formatMoney(parseAmount(item.unit_price), summaryCurrency)}</p>
                        <p className="font-semibold"><span className="text-muted-foreground">Total</span> {formatMoney(parseAmount(item.final_price), summaryCurrency)}</p>
                        {item.has_job && item.job_id != null ? (
                          <Button asChild variant="outline" size="sm" className="mt-2 h-7 px-2 text-xs">
                            <Link to={getJobDetailsPath(item.job_id, item.job_service_instance_id)}>
                              Job {item.job_ref ? `#${item.job_ref}` : item.job_id}
                              <ExternalLink className="ml-1 h-3 w-3" />
                            </Link>
                          </Button>
                        ) : order.job_scope === 'per_order_item' ? (
                          <p className="mt-2 text-xs text-muted-foreground">No job for this line yet</p>
                        ) : null}
                      </div>
                    </div>
                    {includedFeatures.length > 0 || excludedFeatures.length > 0 ? (
                      <div className="mt-3 space-y-2">
                        {includedFeatures.length > 0 ? (
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="text-xs font-medium text-emerald-700">Included</span>
                            {includedFeatures.map((name, featureIndex) => (
                              <Badge key={`inc-${featureIndex}-${name}`} variant="outline" className="border-emerald-300 text-emerald-700">{name}</Badge>
                            ))}
                          </div>
                        ) : null}
                        {excludedFeatures.length > 0 ? (
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="text-xs font-medium text-rose-700">Excluded</span>
                            {excludedFeatures.map((name, featureIndex) => (
                              <Badge key={`exc-${featureIndex}-${name}`} variant="outline" className="border-rose-300 text-rose-700">{name}</Badge>
                            ))}
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                    {/* Media section for this order item */}
                    <OrderItemMedia
                      orderItemId={orderItemId}
                      serviceInstanceId={order.service_instance_id ?? effectiveServiceInstanceId}
                      canEdit={canEditMedia}
                    />
                    <OrderItemAssets
                      orderItemId={orderItemId}
                      customerUserId={
                        (order as { user_id?: string | number | null }).user_id
                        ?? (order as { customer_id?: string | number | null }).customer_id
                        ?? (order as { user_data?: { id?: string | number | null } }).user_data?.id
                        ?? null
                      }
                      serviceInstanceId={order.service_instance_id ?? effectiveServiceInstanceId}
                      canEdit={canEditMedia}
                    />
                  </div>
                )
              })
            )}
          </CardContent>
        </Card>

        {/* Right column: Totals & Fees + Offers */}
        <div className="space-y-4">
          <Card>
            <CardHeader><CardTitle>Totals &amp; Fees</CardTitle></CardHeader>
            <CardContent className="space-y-2 text-sm">
              {order.totals.map((line) => (
                <div key={line.label} className="flex items-center justify-between gap-4">
                  <span className="text-muted-foreground">{line.label}</span>
                  <span className="font-medium">{formatMoney(parseAmount(line.amount), line.currency ?? summaryCurrency)}</span>
                </div>
              ))}
              <Separator />
              <div className="flex items-center justify-between gap-4">
                <span className="text-muted-foreground">Payment mode</span>
                <span>{order.payment_mode_name || order.payment_method || '-'}</span>
              </div>
              <div className="flex items-center justify-between gap-4">
                <span className="text-muted-foreground">Scheduled for</span>
                <span> {renderDate(order.delivery_time ?? order.order_time, "display") as string}</span>
              </div>

            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle>Offers</CardTitle></CardHeader>
            <CardContent className="space-y-2 text-sm">
              {offerRows.length === 0 ? <p className="text-muted-foreground">No offer breakdown saved.</p> : offerRows.map((offer, index) => (
                <div key={`${String(offer.offer_id ?? offer.offer ?? index)}-${index}`} className="rounded-lg border border-emerald-200 bg-emerald-50/70 p-3 text-emerald-950">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-medium">{String(offer.offer ?? offer.title ?? 'Offer')}</p>
                      <p className="text-xs text-emerald-800">{String(offer.description ?? 'Offer applied')}</p>
                    </div>
                    <span className="font-semibold">{formatMoney(parseAmount(offer.discount_amount as string | number | null | undefined), summaryCurrency)}</span>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Booking Instructions */}
      <Card>
        <CardHeader><CardTitle>Booking Instructions</CardTitle></CardHeader>
        <CardContent>
          {order.notes
            ? <p className="rounded-md border border-amber-200 bg-amber-50/60 px-3 py-2 text-sm text-amber-900 italic">{order.notes}</p>
            : <p className="text-sm text-muted-foreground">No instructions provided.</p>
          }
        </CardContent>
      </Card>

      {order.id ? <ComplaintsPanel bookingId={order.id} /> : null}

      {hasLinkedJobs && order.id ? (
        <OrderSettlementSummaryPanel
          orderId={order.id}
          orderReferenceId={referenceId}
          serviceInstanceId={effectiveServiceInstanceId}
          getJobDetailsPath={getJobDetailsPath}
        />
      ) : null}

      {/* Payments — full width at bottom */}
      <Card id="payments" className="scroll-mt-24">
        <CardHeader className="flex flex-row items-center justify-between gap-3">
          <CardTitle className="flex items-center gap-2"><CreditCard className="h-4 w-4" />Payments</CardTitle>
          {canAddPayment ? (
            <Button size="sm" className="gap-2" onClick={() => setIsAddPaymentOpen(true)} disabled={!order.id}>
              <Plus className="h-4 w-4" />Add payment
            </Button>
          ) : null}
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          {paymentsQuery.isLoading ? (
            <p className="text-muted-foreground">Loading payment records...</p>
          ) : paymentsQuery.isError ? (
            <p className="text-destructive">{getErrorMessage(paymentsQuery.error)}</p>
          ) : paymentRows.length === 0 ? (
            <p className="text-muted-foreground">No payment records saved.</p>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {paymentRows.map((payment, index) => (
                <div key={`${payment.payment_reference_id ?? payment.id}-${index}`} className="rounded-lg border border-border/70 p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate font-medium">{getPaymentDisplayReference(payment)}</p>
                      <p className="text-xs text-muted-foreground">{[
                        formatPaymentChannelLabel(payment.payment_service),
                        formatPaymentChannelLabel(payment.payment_mode),
                        formatPaymentChannelLabel(payment.payment_method),
                      ].filter(Boolean).join(' • ') || '-'}</p>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <span className="font-semibold">{formatMoney(parseAmount(payment.amount), summaryCurrency ?? payment.currency)}</span>
                      {canDeletePayments ? (
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8 text-destructive hover:bg-destructive/10 hover:text-destructive"
                          onClick={() => handleDeletePayment(payment.id)}
                          disabled={deletePaymentMutation.isPending}
                          aria-label="Delete payment"
                          title="Delete payment"
                        >
                          {deletePaymentMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                        </Button>
                      ) : null}
                    </div>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-2 text-xs text-muted-foreground">
                    <span>{formatPaymentStatus(payment.payment_status)}</span>
                    <span>{payment.created_on ?? '-'}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Resource planning */}
      <Card>
        <CardHeader><CardTitle>Resource planning</CardTitle></CardHeader>
        <CardContent>
          <OrderResourcePlanningPanel
            order={order}
            referenceId={referenceId}
            serviceInstanceId={effectiveServiceInstanceId}
            showLinkManager={false}
            onChanged={async () => {
              await orderQuery.refetch()
            }}
          />
        </CardContent>
      </Card>

      <Dialog open={isAddPaymentOpen} onOpenChange={setIsAddPaymentOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Add payment</DialogTitle>
            <DialogDescription>Select a payment method from configured wallets and enter the amount collected.</DialogDescription>
          </DialogHeader>
          <div className="space-y-5">
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="rounded-lg border border-border/70 p-3">
                <p className="text-xs text-muted-foreground">Balance</p>
                <p className="text-xl font-semibold">{formatMoney(balanceAmount ?? fallbackBalanceAmount, summaryCurrency)}</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="add-payment-amount">Amount</Label>
                <Input
                  id="add-payment-amount"
                  inputMode="decimal"
                  value={paymentAmount}
                  onChange={(event) => setPaymentAmount(event.target.value)}
                  placeholder="0.00"
                />
              </div>
            </div>

            <div className="space-y-3">
              <Label>Payment method</Label>
              {!walletOrgInstanceId ? (
                <p className="rounded-lg border border-border/70 p-3 text-sm text-muted-foreground">Organisation context is required before adding payment.</p>
              ) : walletMethodsQuery.isLoading ? (
                <p className="flex items-center text-sm text-muted-foreground"><Loader2 className="mr-2 h-4 w-4 animate-spin" />Loading payment methods...</p>
              ) : walletMethodsQuery.isError ? (
                <p className="text-sm text-destructive">{getErrorMessage(walletMethodsQuery.error)}</p>
              ) : walletMethods.length === 0 ? (
                <p className="rounded-lg border border-border/70 p-3 text-sm text-muted-foreground">No payment methods configured in wallet list.</p>
              ) : (
                <div className="grid gap-2 sm:grid-cols-2">
                  {walletMethods.map((wallet) => {
                    const label = getWalletLabel(wallet)
                    const selected = selectedWalletId === String(wallet.id)
                    return (
                      <button
                        key={String(wallet.id)}
                        type="button"
                        onClick={() => setSelectedWalletId(String(wallet.id))}
                        className={`rounded-lg border p-3 text-left text-sm transition ${selected ? 'border-primary bg-primary/5 ring-2 ring-primary/20' : 'border-border bg-background hover:bg-accent/30'}`}
                      >
                        <span className="block font-medium">{label}</span>
                        <span className="text-xs text-muted-foreground">{wallet.acc_name || wallet.code || 'Wallet method'}</span>
                      </button>
                    )
                  })}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddPaymentOpen(false)}>Cancel</Button>
            <Button
              onClick={() => createPaymentMutation.mutate()}
              disabled={!selectedWalletId || parseAmount(paymentAmount) <= 0 || createPaymentMutation.isPending}
            >
              {createPaymentMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Add payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isResendConfirmationOpen} onOpenChange={setIsResendConfirmationOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Resend confirmation email</DialogTitle>
            <DialogDescription>
              Sends the order confirmation email to the customer for booking #{order.reference_id}. You can override
              the recipient if the original address was wrong.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="resend-confirmation-email">Recipient email</Label>
            <Input
              id="resend-confirmation-email"
              type="email"
              value={resendEmail}
              onChange={(event) => setResendEmail(event.target.value)}
              placeholder="customer@example.com"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsResendConfirmationOpen(false)}>Cancel</Button>
            <Button
              onClick={() => resendConfirmationMutation.mutate()}
              disabled={!resendEmail.trim() || resendConfirmationMutation.isPending}
            >
              {resendConfirmationMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Mail className="mr-2 h-4 w-4" />}
              Send email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
